// Cloud posture is built from the Cloud scans audits: one entry per account, scored from its newest finished audit.
import { accountKey, accountMeta, accountName } from './cloudScanLogic';
import { appRisk } from './mobileScanLogic';

const DONE = ['completed', 'finished'];
const LIVE = ['running', 'pending'];

// "key: value" lines in a finding's evidence -> { key: value }
const evidenceFields = (text) => Object.fromEntries(String(text || '').split('\n').map((l) => { const i = l.indexOf(':'); return i > 0 ? [l.slice(0, i).trim().toLowerCase(), l.slice(i + 1).trim()] : null; }).filter(Boolean));

// One audit finding in plain terms.
export function parseFinding(f) {
  const ev = evidenceFields(f.evidence);
  const raw = f.raw_data?.raw || f.raw_data || {};
  const pick = (...vals) => vals.map((v) => (v == null ? '' : String(v).trim())).find(Boolean) || '';
  return {
    id: f.id, rule: f.title, severity: f.severity, scan: f.scan,
    resource: pick(ev.resource, ev.resource_id, raw.resource, raw.resource_id),
    service: pick(ev.service, raw.service),
    region: pick(ev.region, raw.region),
    category: pick(raw.category, raw.Category, ev.service, raw.service) || 'Other',
    fix: pick(ev.remediation, raw.remediation, raw.recommendation),
    when: f.created_at
  };
}

// -> accounts [{ key, name, meta, provider, userTarget, latest, live, done, prev, risk, prevRisk, runs, findings }]
export function buildPosture(scans, findings) {
  const byScan = {};
  findings.forEach((f) => { (byScan[String(f.scan)] = byScan[String(f.scan)] || []).push(f); });
  const groups = new Map();
  [...scans].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).forEach((s) => {
    const k = accountKey(s);
    const g = groups.get(k) || { key: k, name: accountName(s), meta: accountMeta(s), provider: s.options?.cloud_provider || 'aws', userTarget: s.user_target, latest: s, live: null, doneScans: [], runs: 0 };
    g.runs += 1;
    if (!g.live && LIVE.includes(s.status)) g.live = s;
    if (DONE.includes(s.status)) g.doneScans.push(s);
    groups.set(k, g);
  });
  return [...groups.values()].map((g) => {
    const done = g.doneScans[0] || null;
    const prev = g.doneScans[1] || null;
    const list = done ? (byScan[String(done.id)] || []) : [];
    return {
      ...g, done, prev, findings: list.map(parseFinding),
      risk: done ? appRisk(list) : null, prevRisk: prev ? appRisk(byScan[String(prev.id)] || []) : null
    };
  });
}

// Count of findings per category, biggest first. Empty when nothing is categorised.
export function byCategory(items) {
  const m = {};
  items.forEach((i) => { m[i.category] = (m[i.category] || 0) + 1; });
  const rows = Object.entries(m).map(([label, n]) => ({ label, n })).sort((a, b) => b.n - a.n);
  return rows.length === 1 && rows[0].label === 'Other' ? [] : rows;
}

export const overall = (accounts) => {
  const scored = accounts.filter((a) => a.risk);
  if (!scored.length) return null;
  const score = Math.round(scored.reduce((s, a) => s + a.risk.score, 0) / scored.length);
  return { score, grade: score >= 90 ? 'A' : score >= 80 ? 'B' : score >= 70 ? 'C' : score >= 60 ? 'D' : 'F', count: scored.length };
};
